/**
 * night_sky — the night sky's BACKGROUND in TSL (the night-sky realism wave, 2026-10-06): the natural sky (airglow,
 * zodiacal light, integrated starlight — never black), the ARTIFICIAL sky (the zenith skyglow and the K light domes of
 * lib/light_pollution, from `skyglowLive()`), the Milky Way (the starless photo, calibrated to sky units and faded by
 * its contrast), and the per-pixel quantities the star field needs: the local limiting magnitude, the extinction and
 * the display luminance a star's contrast is drawn against. The arithmetic is lib/sky/night_sky_model.ts's (pure,
 * the CPU mirror) — change one, change both; `verify:night-sky-model` pins the constants.
 *
 * Everything here is in SKY UNITS (1 = the natural zenith, `UNIT_NITS` cd/m²) until the caller multiplies by
 * `unitScene` (= UNIT_NITS × luxToScene, written every frame — THE PHYSICAL-EMISSIVE CONTRACT).
 *
 * PLACEMENT: `buildNightSkyBase` must be called inside the composite's top-level `Fn`, outside any per-pixel branch:
 * every value it returns is `.toVar()`'d there (THE LAZY-SAMPLE LAW — the star loop reads them inside its branch).
 *
 * NO-CYCLE RULE: parameters in, nodes out; this file imports nothing from SkyModule.
 */
import {
  float, vec3, clamp, max, min, mix, smoothstep, sqrt, exp, exp2, log2, pow, dot, length, acos, atan, luminance,
  texture as tslTexture, equirectUV,
} from 'three/tsl';
import type { Texture } from 'three/webgpu';
import {
  NAT_HORIZON_GAIN, NAT_TINT_ZENITH, NAT_TINT_HORIZON, EXT_K, MW_FLOOR, MW_UNIT, MW_COMPRESS, MW_MAX, MW_SATURATION,
  MW_C_GONE, MW_C_FULL, ART_K,
} from './night_sky_model.js';
import { OVERCAST_AMPLIFICATION, OVERCAST_NATURAL_LEFT, OVERCAST_TINT_SHIFT } from './sky_sampler.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** The K = 4 domes, as uniforms: vec4(dirX, dirZ, peak, halfWidth), a vec4 of their four heights, a vec3 tint each. */
export const NIGHT_DOMES = 4;

export interface NightSkyUniforms {
  /** Unit direction to the sun (the deep-night gate of the natural sky). */
  sunDir: N;
  /** Scene units per sky unit (UNIT_NITS × luxToScene). */
  unitScene: N;
  /** THE NIGHT-FLOOR LIFT (night_sky_model.naturalSkyLift). */
  lift: N;
  /** vec4(ratio, tint r, g, b) — the zenith skyglow over the camera. */
  glow: N;
  /** Per dome: vec4(dirX, dirZ, peak, halfWidth) and a vec3 tint; `domeHeight` = vec4 of the four heights (rad). */
  domes: N[];
  domeTints: N[];
  domeHeight: N;
  /** Milky Way gain (lever; 1 = calibrated). */
  mwGain: N;
  /** THE NIGHT DISPLAY GAIN on every display term — the natural sky, the adapted skyglow, the Milky Way and (through bgDisp) the stars (night_sky_model). Absent = 1. */
  natGain?: N;
  /**
   * THE DECK (2026-10-06, agent A's open item 3): the cloudiness over the camera (0..1). A deck over a lit town reflects
   * its upward light back down — the artificial sky ×(1 + (OVERCAST_AMPLIFICATION·deckShape − 1)·deck), reddened — and
   * hides the airglow above it (×(1 − (1 − OVERCAST_NATURAL_LEFT)·deck)): sky_sampler.nightSkyRadiance is its CPU mirror,
   * so the sky between the clouds, the fog and the clouds' undersides agree. Absent = 0.
   */
  deck?: N;
}

export interface NightSkyBase {
  /** The sky's display radiance, sky units, vec3 ((natural + the adapted artificial) × lift + Milky Way; no stars). */
  rgb: N;
  /** The display luminance stars are drawn against, sky units ((natural + adapted artificial) × lift + twilight + moon). */
  bgDisp: N;
  /** The physical luminance the eye's threshold sees, sky units, and its naked-eye limiting magnitude. */
  physical: N;
  nelm: N;
  /** Extinction toward this direction: magnitudes, and the colour (luminance-normalised reddening). */
  extMag: N;
  extTint: N;
}

/** Naked-eye limiting magnitude of a sky of `units` (Schaefer / Crumey via the contract's nelmFromSqm; SQM = 22 − 2.5·log10 units). */
export function nelmNode(units: N): N {
  // 10^(4.316 − SQM/5) = 10^(−0.084)·√units  →  NELM = 7.93 − 5·log10(0.8241·√units + 1)
  return float(7.93).sub(log2(sqrt(max(units, float(1e-6))).mul(0.82414).add(1)).mul(5 * 0.30103));
}

/** One dome's excess radiance along `v` (lib/light_pollution.domeRadiance in TSL). */
function domeNode(v: N, d: N, height: N): N {
  const h = length(vec3(v.x, 0, v.z));
  const elev = atan(max(v.y, 0), max(h, 1e-6));
  const hasDir = smoothstep(1e-4, 1e-3, length(vec3(d.x, 0, d.y)));
  const c = clamp(v.x.mul(d.x).add(v.z.mul(d.y)).div(max(h, 1e-6)), -1, 1);
  const dAz = acos(c).div(max(d.w, 1e-4));
  const azW = mix(float(1), exp(dAz.mul(dAz).mul(-0.5)), hasDir);
  const hh = max(height, 1e-3);
  const top = exp(float(-Math.PI / 2).div(hh));
  return d.z.mul(azW).mul(max(float(0), exp(elev.negate().div(hh)).sub(top).div(float(1).sub(top))));
}

/**
 * The sky's per-pixel base. `atmo` and `moonSky` are the scene's twilight / moonlit sky in SCENE units (vec3, toVar'd
 * by the caller); `bgTex` the starless photo (null = no Milky Way); `pxSize` the pixel's angular footprint (radians, from
 * the caller's hoisted derivatives) — the photo's mip level.
 */
export function buildNightSkyBase(viewDir: N, atmo: N, moonSky: N, bgTex: Texture | null, u: NightSkyUniforms, pxSize?: N): NightSkyBase {
  const y = (viewDir as { y: N }).y;
  const s = clamp(y, 0, 1);
  const c = float(1).sub(s);
  const c2 = c.mul(c);
  // ── deep night (lib/lighting deepNight): the natural sky's own gate ──
  const dn0 = clamp(float(-0.10).sub(u.sunDir.y).div(0.18), 0, 1);
  const deepNight = dn0.mul(dn0).mul(float(3).sub(dn0.mul(2)));
  // ── the natural sky (night_sky_model.naturalShape / naturalTint) ──
  const nat = float(1).add(c2.mul(c).mul(NAT_HORIZON_GAIN)).toVar('nightNat');
  const natTint = mix(vec3(...NAT_TINT_ZENITH), vec3(...NAT_TINT_HORIZON), c2.mul(c2));
  // ── the artificial sky (light_pollution.artificialSkyRatio: the glow × (1 + 1.2·(1 − s)²) + the domes) ──
  const g = u.glow.x.mul(float(1).add(c2.mul(1.2)));
  let art: N = g;
  let artRgb: N = vec3(u.glow.y, u.glow.z, u.glow.w).mul(g);
  const hts = [u.domeHeight.x, u.domeHeight.y, u.domeHeight.z, u.domeHeight.w];
  for (let k = 0; k < NIGHT_DOMES; k++) {
    const r = domeNode(viewDir, u.domes[k], hts[k]);
    art = art.add(r);
    artRgb = artRgb.add(u.domeTints[k].mul(r));
  }
  // THE DECK: amplify and redden the physical artificial sky before the display maps it (sky_sampler.nightSkyRadiance).
  const deck = clamp(u.deck ?? float(0), 0, 1);
  const amp = float(1).add(float(OVERCAST_AMPLIFICATION).mul(float(0.55).add(s.mul(0.45))).sub(1).mul(deck));
  const shifted = artRgb.mul(vec3(...OVERCAST_TINT_SHIFT));
  artRgb = mix(artRgb, shifted.mul(luminance(artRgb).div(max(luminance(shifted), 1e-6))), deck).mul(amp);
  art = art.mul(amp);
  const natLeft = float(1).sub(deck.mul(1 - OVERCAST_NATURAL_LEFT));
  const artV = art.toVar('nightArt');
  // Under the horizon line the sky holds its horizon value, then fades (the ground covers it; a summit's dip shows it).
  const below = smoothstep(-0.14, -0.05, y);
  // ── the twilight and the moonlit sky in units (they raise the threshold like any sky) ──
  const inv = float(1).div(max(u.unitScene, float(1e-12)));
  const atmoU = luminance(atmo).mul(inv);
  const moonU = luminance(moonSky).mul(inv);
  const physical = max(nat.mul(deepNight).mul(natLeft).add(artV).add(atmoU).add(moonU), 1e-4).toVar('nightPhys');
  const nelm = nelmNode(physical).toVar('nightNelm');
  const natGain = u.natGain ?? float(1);
  const natDisp = nat.mul(u.lift).mul(deepNight).mul(natGain).mul(natLeft);
  // THE ADAPTED ARTIFICIAL SKY (night_sky_model.artificialDisplayUnits): ART_K·ln(1 + art/ART_K), × the lift.
  const artDisp = log2(artV.div(ART_K).add(1)).mul(ART_K * Math.LN2).mul(u.lift).mul(natGain).toVar('nightArtDisp');
  const artScale = artDisp.div(max(artV, 1e-4)); // → the lift as art → 0 (the colour keeps its tint)
  const bgDisp = max(natDisp.add(artDisp).add(atmoU).add(moonU), 1e-4).toVar('nightBgDisp');
  // ── extinction (night_sky_model.extinctionMag; the colour is the old night_live reddening, luminance-normalised) ──
  const airmass = min(float(1).div(max(y, 0.035)), 14);
  const extMag = airmass.sub(1).mul(EXT_K).toVar('nightExtMag');
  const red = pow(vec3(0.935, 0.905, 0.865), airmass.sub(1));
  const extTint = red.div(max(luminance(red), 1e-4)).toVar('nightExtTint');
  // ── the Milky Way: photo excess → units (compressed past one), faded by its contrast over the PHYSICAL sky ──
  let mw: N = vec3(0);
  if (bgTex) {
    // FIX (2026-10-06): the photo's mip level is EXPLICIT — the implicit one took atan2's jump at the equirect's seam
    // (u 1 → 0 on −X) as a huge derivative and drew a column of its smallest mip down the sky there (seen in the bed).
    const W = ((bgTex.image as { width?: number } | undefined)?.width ?? 4096);
    const lod = pxSize ? max(log2(pxSize.mul(W / (2 * Math.PI))), 0) : float(0);
    const ph = tslTexture(bgTex, equirectUV(viewDir), lod).rgb;
    const pl = max(luminance(ph), 1e-6);
    const x = max(pl.sub(MW_FLOOR), 0).div(MW_UNIT);
    const units = min(mix(x, pow(max(x, 1e-6), MW_COMPRESS), smoothstep(0.9, 1.1, x)), MW_MAX);
    const vis = smoothstep(MW_C_GONE, MW_C_FULL, units.div(physical));
    const chroma = mix(vec3(1), ph.div(pl), MW_SATURATION);
    // Extincted like the stars it is made of: 10^(−0.4·extMag), reddened.
    const tr = exp2(extMag.mul(-0.4 * 3.321928));
    mw = chroma.mul(units).mul(vis).mul(tr).mul(extTint).mul(u.lift).mul(deepNight).mul(u.mwGain).mul(natGain).mul(natLeft);
  }
  const rgb = natTint.mul(natDisp).add(artRgb.mul(artScale)).mul(below).add(mw).toVar('nightSkyRgb');
  return { rgb, bgDisp, physical, nelm, extMag, extTint };
}

type V4 = { set(x: number, y: number, z: number, w: number): unknown };
type V3 = { set(x: number, y: number, z: number): unknown };
const valOf = <T>(un: N): T => (un as { value: T }).value;
/**
 * Write the night sky's uniforms (the game's SkyModule and the sky bed share it): sky units into the scene, the lift,
 * the zenith glow and up to NIGHT_DOMES domes (strongest first; the rest zeroed).
 */
export function writeNightSkyUniforms(u: NightSkyUniforms, v: {
  unitScene: number; lift: number;
  glow: { ratio: number; tint: readonly number[] };
  domes: ReadonlyArray<{ dirX: number; dirZ: number; peak: number; halfWidth: number; height: number; tint: readonly number[] }>;
}): void {
  (u.unitScene as { value: number }).value = v.unitScene;
  (u.lift as { value: number }).value = v.lift;
  valOf<V4>(u.glow).set(v.glow.ratio, v.glow.tint[0], v.glow.tint[1], v.glow.tint[2]);
  const h = [0.1, 0.1, 0.1, 0.1];
  for (let i = 0; i < NIGHT_DOMES; i++) {
    const d = v.domes[i];
    if (d) {
      valOf<V4>(u.domes[i]).set(d.dirX, d.dirZ, d.peak, d.halfWidth);
      valOf<V3>(u.domeTints[i]).set(d.tint[0], d.tint[1], d.tint[2]);
      h[i] = d.height;
    } else valOf<V4>(u.domes[i]).set(0, 0, 0, 1);
  }
  valOf<V4>(u.domeHeight).set(h[0], h[1], h[2], h[3]);
}
